import { useEffect, useRef } from "react";
import RichTextEditor, { BaseKit } from "reactjs-tiptap-editor";
import { Node, mergeAttributes } from "@tiptap/core";
import { Image } from "reactjs-tiptap-editor/image";
import { Video } from "reactjs-tiptap-editor/video";
import { Blockquote } from "reactjs-tiptap-editor/blockquote";
import { Bold } from "reactjs-tiptap-editor/bold";
import { Italic } from "reactjs-tiptap-editor/italic";
import { Strike } from "reactjs-tiptap-editor/strike";
import { Heading } from "reactjs-tiptap-editor/heading";
import { Link } from "reactjs-tiptap-editor/link";
import { TextAlign } from "reactjs-tiptap-editor/textalign";
import { TextUnderline } from "reactjs-tiptap-editor/textunderline";
import { TrailingNode } from "reactjs-tiptap-editor/trailingnode";
import { TextBubble } from "reactjs-tiptap-editor/textbubble";
import { OrderedList } from "reactjs-tiptap-editor/orderedlist";
import { History } from "reactjs-tiptap-editor/history";
import { Highlight } from "reactjs-tiptap-editor/highlight";
import { Clear } from "reactjs-tiptap-editor/clear";
import { Color } from "reactjs-tiptap-editor/color";
import { FontFamily } from "reactjs-tiptap-editor/fontfamily";
import { FontSize } from "reactjs-tiptap-editor/fontsize";
import { BulletList } from "reactjs-tiptap-editor/bulletlist";
import { Plugin, PluginKey } from "prosemirror-state";
import "reactjs-tiptap-editor/style.css";

const openInNewTabKey = new PluginKey("open-in-new-tab");

const CustomLink = Link.extend({
  renderHTML({ HTMLAttributes }) {
    const { href, target, rel, ...rest } = HTMLAttributes;

    return [
      "a",
      {
        ...rest,
        "data-href": href,
        role: "link",
        tabindex: "0",
        contenteditable: "false",
        draggable: "false",
      },
      0,
    ];
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      contenteditable: { default: "false" },
      draggable: { default: false },
      tabindex: { default: "0" },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: openInNewTabKey,
        props: {
          handleClick(view, pos, event) {
            const el = event.target;
            if (!(el instanceof HTMLAnchorElement)) return false;

            const href = el.getAttribute("href");
            if (!href) return false;

            event.preventDefault();
            window.open(href, "_blank", "noopener,noreferrer");
            return true;
          },

          handleKeyDown(view, event) {
            if (event.key !== "Enter") return false;

            const { state } = view;
            const linkMark = state.selection.$from
              .marks()
              .find((m) => m.type.name === "link");

            if (!linkMark) return false;

            event.preventDefault();
            window.open(linkMark.attrs.href, "_blank", "noopener,noreferrer");
            return true;
          },
        },
      }),
    ];
  },
});

const CustomParagraph = Node.create({
  name: "paragraph",
  group: "block",
  content: "inline*",
  parseHTML() {
    return [{ tag: "p" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "p",
      mergeAttributes(HTMLAttributes, { contenteditable: "false" }),
      0,
    ];
  },
});

const extensions = [
  BaseKit.configure({
    characterCount: false,
    actions: false,
    placeholder: false,
  }),
  History,
  Clear,
  FontFamily,
  Heading,
  FontSize,
  Bold,
  Italic,
  Strike,
  TextUnderline,
  Color,
  Highlight,
  BulletList,
  OrderedList,
  TextAlign,
  CustomLink.configure({
    openOnClick: false,
  }),
  Blockquote,
  TrailingNode,
  TextBubble,
  Image,
  Video.configure({ width: "600" }),
  CustomParagraph,
];

const MessageContent = ({ content, fallback }) => {
  const containerRef = useRef(null);
  const html = typeof content === "string" ? content.trim() : "";

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const openLink = (el) => {
      const url = el.getAttribute("data-href") || el.getAttribute("href");
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    };

    const handler = (event) => {
      const link = event.target.closest("a[data-href], a[href]");
      if (!link || !container.contains(link)) return;
      event.preventDefault();
      event.stopPropagation();
      openLink(link);
    };

    const keyHandler = (event) => {
      if (event.key === "Enter" || event.key === " ") handler(event);
    };

    container.addEventListener("click", handler, true);
    container.addEventListener("keydown", keyHandler, true);
    return () => {
      container.removeEventListener("click", handler, true);
      container.removeEventListener("keydown", keyHandler, true);
    };
  }, [html]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const interval = setInterval(() => {
      const editors = container.querySelectorAll(
        ".richtext-relative .tiptap.ProseMirror"
      );
      if (!editors.length) return;
      editors.forEach((editorDiv) => {
        editorDiv.setAttribute("contenteditable", "false");
        editorDiv.setAttribute("tabindex", "-1");
      });
      clearInterval(interval);
    }, 50);

    return () => clearInterval(interval);
  }, [html]);

  if (!html) {
    return (
      <p className="edsimsV2-message-fallback">
        {fallback || "No content available."}
      </p>
    );
  }

  return (
    <div
      className="edsimsV2-message-content richtext-inert"
      role="region"
      aria-label="Message content"
      ref={containerRef}
    >
      <RichTextEditor
        content={html}
        extensions={extensions}
        onChange={() => {}}
        toolbar={false}
        editable={false}
        output="html"
        dark={false}
      />
    </div>
  );
};

export default MessageContent;
